import { useEffect, useState } from 'react';
import cn from 'classnames';

import scrollingElement from 'js/utils/scrolling-element';

import TabList from './TabList';
import TabListItem from './TabListItem';
import TabContentWrapper from './TabContentWrapper';

import { ITabs, ITabs_Items } from 'source/NFR.types';

interface CustomTabs extends ITabs_Items {
  disabled?: boolean;
}

const tabId = (id: string) => `tab-${id}`;
const contentId = (id: string) => `content-${id}`;

// NOTE: 'aria-role="tablist"' is intentionally not used for this component.
// Read about why here: https://simplyaccessible.com/article/danger-aria-tabs/

const Tabs: React.FC<ITabs> = ({ activeTab, items = [] }) => {
  const initialId = activeTab || (items[0] || {}).guid;
  const [currentTab, setCurrentTab] = useState(initialId);
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const [isMounted, setIsMounted] = useState(false);

  const setActiveTab = (id: string) => (e: React.MouseEvent<HTMLElement>) => {
    // NOTE: The anchor link scroll is prevented because it's intrusive for mouse user. Focus is set manually in useEffect below.
    e.preventDefault();
    setCurrentTab(id);
    setCurrentTabIndex(items.findIndex(item => item.guid === id));
  };

  useEffect(() => {
    // NOTE: This callback is called for the initial render, resulting in the first open tab receiving focus without user interaction. This is not nice, so this flag is used to prevent focus before interaction.
    if (!isMounted) {
      setIsMounted(true);
      return;
    }
    const url = new URL(window.location.href);
    url.search = '';
    url.hash = currentTab;
    window.history.pushState({}, '', url);
    // NOTE: Manually setting scroll position after focus in order to prevent scroll consistently acro´¨ss browsers
    if (!scrollingElement) return;

    const content = document.getElementById(contentId(currentTab));
    const scrollPos = scrollingElement.scrollTop;
    if (content) {
      content.focus();
    }
    scrollingElement.scrollTop = scrollPos;
  }, [currentTab, isMounted]);

  useEffect(() => {
    //Manually anchor-scroll + select tab
    const hash = window.location.hash.replace('#', '');
    const hashMatchesTab = items.some(({ guid }) => guid === hash);
    if (hashMatchesTab) {
      setCurrentTab(hash);
      setCurrentTabIndex(items.findIndex(item => item.guid === hash));

      const tab = document.getElementById(tabId(hash));

      const scrollToTab = () => {
        setTimeout(() => {
          // if (!scrollingElement) return;
          const el = scrollingElement as HTMLElement;
          const behavior = el.style.scrollBehavior;
          el.style.scrollBehavior = 'auto';
          el.scrollTop += tab?.getBoundingClientRect().top ?? 0;
          el.style.scrollBehavior = behavior;
        }, 100); // 100ms was arrived at through testing as there doesn't seem to be any way to trigger scrolling at the exact time nothing else can interfere with it.
      };
      window.addEventListener('load', scrollToTab, { once: true });
    }
  }, [items]);

  if (items.length <= 1) {
    return (
      <div className="no-tabs">
        <TabContentWrapper {...items[0].content} />
      </div>
    );
  } else {
    return (
      <div className="tabs">
        <TabList className="tab-list--limited-width">
          {(items as CustomTabs[])?.map(({ guid, name, disabled }, index) => {
            const isCurrent = guid === currentTab;
            return (
              <TabListItem
                key={guid}
                isCurrent={isCurrent}
                length={items.length}
                index={index}
                currentIndex={currentTabIndex}
                disabled={disabled}
              >
                <a
                  className={cn({ 'tab-list__link--has-hover-effect': !disabled })}
                  aria-current={isCurrent}
                  href={`#${guid}`}
                  id={tabId(guid)}
                  onClick={setActiveTab(guid)}
                >
                  {name}
                </a>
              </TabListItem>
            );
          })}
        </TabList>
        <div className="tabs--content">
          {items?.map(tab => {
            const isCurrent = tab.guid === currentTab;

            return (
              <section
                aria-hidden={!isCurrent}
                aria-labelledby={tabId(tab.guid)}
                id={contentId(tab.guid)}
                key={tab.name}
                className={cn('tabs--content-tab', { 'tabs--content-tab--inactive': !isCurrent })}
                tabIndex={isCurrent ? -1 : undefined}
              >
                <TabContentWrapper {...tab.content} />
              </section>
            );
          })}
        </div>
      </div>
    );
  }
};

Tabs.displayName = 'Tabs';

export default Tabs;
